import { lazy } from 'react';
import { Navigate, createBrowserRouter } from 'react-router-dom';
import { AppShell } from '@/app/layout/app-shell';

import { RouteErrorBoundary } from '@/app/routes/error-route';
import { LegacyCustomerRedirect } from '@/app/routes/legacy-customer-redirect';

import { LOGIN_PATH, PermissionGuard, RequireAuth } from '@/features/auth';

import { RequirePortalAuth } from '@/features/customer-portal';
import {
  FeatureAvailabilityProvider,
  PortalFeatureAvailabilityProvider,
} from '@/features/platform-features';

const InventoryOverviewRoute = lazy(async () => ({
  default: (await import('@/features/inventory-overview/routes/inventory-overview-route'))
    .InventoryOverviewRoute,
}));

const IntroductionRoute = lazy(async () => ({
  default: (await import('@/features/introduction/introduction')).IntroductionRoute,
}));
const ConsoleLayout = lazy(async () => ({
  default: (await import('@/app/layout/console-layout')).ConsoleLayout,
}));
const DashboardRoute = lazy(async () => ({
  default: (await import('@/app/routes/dashboard-route')).DashboardRoute,
}));
const AdminAuditVaultRoute = lazy(async () => ({
  default: (await import('@/features/admin/routes/admin-audit-vault-route')).AdminAuditVaultRoute,
}));
const AdminBackupsRoute = lazy(async () => ({
  default: (await import('@/features/admin/routes/admin-backups-route')).AdminBackupsRoute,
}));
const AdminCustomFieldsRoute = lazy(async () => ({
  default: (await import('@/features/admin/routes/admin-custom-fields-route'))
    .AdminCustomFieldsRoute,
}));
const AdminDataHubRoute = lazy(async () => ({
  default: (await import('@/features/admin/routes/admin-data-hub-route')).AdminDataHubRoute,
}));
const AdminErrorLogsRoute = lazy(async () => ({
  default: (await import('@/features/admin/routes/admin-error-logs-route')).AdminErrorLogsRoute,
}));
const AdminLayout = lazy(async () => ({
  default: (await import('@/features/admin/components/admin-layout')).AdminLayout,
}));
const AdminMailBackupsRoute = lazy(async () => ({
  default: (await import('@/features/admin/routes/admin-mail-backups-route')).AdminMailBackupsRoute,
}));
const AdminSystemRoute = lazy(async () => ({
  default: (await import('@/features/admin/routes/admin-system-route')).AdminSystemRoute,
}));
const LoginRoute = lazy(async () => ({
  default: (await import('@/features/auth/routes/login-route')).LoginRoute,
}));
const DocumentEditorRoute = lazy(async () => ({
  default: (await import('@/features/documents/routes/document-editor-route')).DocumentEditorRoute,
}));
const DocumentsRoute = lazy(async () => ({
  default: (await import('@/features/documents/routes/documents-route')).DocumentsRoute,
}));
const LegacyDocumentsRoute = lazy(async () => ({
  default: (await import('@/features/legacy-documents/routes/legacy-documents-route'))
    .LegacyDocumentsRoute,
}));
const ReturnsRoute = lazy(async () => ({
  default: (await import('@/features/returns/routes/returns-route')).ReturnsRoute,
}));
const ReusableContainersRoute = lazy(async () => ({
  default: (await import('@/features/reusable-containers/routes/reusable-containers-route'))
    .ReusableContainersRoute,
}));
const PickingRoute = lazy(async () => ({
  default: (await import('@/features/picking/routes/picking-route')).PickingRoute,
}));
const PickingDetailRoute = lazy(async () => ({
  default: (await import('@/features/picking/routes/picking-route')).PickingDetailRoute,
}));
const InventorySessionsRoute = lazy(async () => ({
  default: (await import('@/features/inventory-sessions/routes/inventory-sessions-route'))
    .InventorySessionsRoute,
}));
const InventorySessionDetailRoute = lazy(async () => ({
  default: (await import('@/features/inventory-sessions/routes/inventory-sessions-route'))
    .InventorySessionDetailRoute,
}));
const WarehouseImportRoute = lazy(async () => ({
  default: (await import('@/features/warehouse-import/routes/warehouse-import-route'))
    .WarehouseImportRoute,
}));
const DeliveryToursRoute = lazy(async () => ({
  default: (await import('@/features/delivery-tours/routes/delivery-tours-route'))
    .DeliveryToursRoute,
}));
const DeliveryTourDetailRoute = lazy(async () => ({
  default: (await import('@/features/delivery-tours/routes/delivery-tours-route'))
    .DeliveryTourDetailRoute,
}));
const ElectronicInvoicesRoute = lazy(async () => ({
  default: (await import('@/features/electronic-invoices/routes/electronic-invoices-route'))
    .ElectronicInvoicesRoute,
}));
const OperationalSettingsRoute = lazy(async () => ({
  default: (await import('@/features/operations/routes/operational-settings-route'))
    .OperationalSettingsRoute,
}));
const OperationsAlertsRoute = lazy(async () => ({
  default: (await import('@/features/operations/routes/operations-alerts-route'))
    .OperationsAlertsRoute,
}));
const PartnerCustomersRoute = lazy(async () => ({
  default: (await import('@/features/partners/routes/partner-customers-route'))
    .PartnerCustomersRoute,
}));
const PartnerDetailRoute = lazy(async () => ({
  default: (await import('@/features/partners/routes/partner-detail-route')).PartnerDetailRoute,
}));
const PartnerProspectsRoute = lazy(async () => ({
  default: (await import('@/features/partners/routes/partner-prospects-route'))
    .PartnerProspectsRoute,
}));
const PartnerSuppliersRoute = lazy(async () => ({
  default: (await import('@/features/partners/routes/partner-suppliers-route'))
    .PartnerSuppliersRoute,
}));
const PartnersRoute = lazy(async () => ({
  default: (await import('@/features/partners/routes/partners-route')).PartnersRoute,
}));
const PartnerMapRoute = lazy(async () => ({
  default: (await import('@/features/partners/routes/partner-map-route')).PartnerMapRoute,
}));
const BatchDetailRoute = lazy(async () => ({
  default: (await import('@/features/products/routes/batch-detail-route')).BatchDetailRoute,
}));
const ProductDetailRoute = lazy(async () => ({
  default: (await import('@/features/products/routes/product-detail-route')).ProductDetailRoute,
}));
const ProductsRoute = lazy(async () => ({
  default: (await import('@/features/products/routes/products-route')).ProductsRoute,
}));
const NotificationSettingsRoute = lazy(async () => ({
  default: (await import('@/features/notifications/routes/notification-settings-route'))
    .NotificationSettingsRoute,
}));
const ProductNewsRoute = lazy(async () => ({
  default: (await import('@/features/product-news/routes/product-news-route')).ProductNewsRoute,
}));
const NewsletterConfirmRoute = lazy(async () => ({
  default: (await import('@/features/newsletter/routes/newsletter-confirm-route'))
    .NewsletterConfirmRoute,
}));
const NewsletterPreferencesRoute = lazy(async () => ({
  default: (await import('@/features/newsletter/routes/newsletter-preferences-route'))
    .NewsletterPreferencesRoute,
}));
const NewsletterRoute = lazy(async () => ({
  default: (await import('@/features/newsletter/routes/newsletter-route')).NewsletterRoute,
}));
const NewsletterSubscribeRoute = lazy(async () => ({
  default: (await import('@/features/newsletter/routes/newsletter-subscribe-route'))
    .NewsletterSubscribeRoute,
}));
const CustomerQuestionnaireRoute = lazy(async () => ({
  default: (await import('@/features/questionnaires/routes/customer-questionnaire-route'))
    .CustomerQuestionnaireRoute,
}));
const FieldCockpitRoute = lazy(async () => ({
  default: (await import('@/features/questionnaires/routes/field-cockpit-route')).FieldCockpitRoute,
}));
const QuestionnaireBuilderRoute = lazy(async () => ({
  default: (await import('@/features/questionnaires/routes/questionnaire-builder-route'))
    .QuestionnaireBuilderRoute,
}));
const QuestionnaireResultDetailRoute = lazy(async () => ({
  default: (await import('@/features/questionnaires/routes/questionnaire-result-detail-route'))
    .QuestionnaireResultDetailRoute,
}));
const QuestionnaireResultsRoute = lazy(async () => ({
  default: (await import('@/features/questionnaires/routes/questionnaire-results-route'))
    .QuestionnaireResultsRoute,
}));
const QuestionnaireRunsRoute = lazy(async () => ({
  default: (await import('@/features/questionnaires/routes/questionnaire-runs-route'))
    .QuestionnaireRunsRoute,
}));
const QuestionnaireRunRoute = lazy(async () => ({
  default: (await import('@/features/questionnaires/routes/questionnaire-run-route'))
    .QuestionnaireRunRoute,
}));
const QuestionnaireRunStartRoute = lazy(async () => ({
  default: (await import('@/features/questionnaires/routes/questionnaire-run-start-route'))
    .QuestionnaireRunStartRoute,
}));
const QuestionnaireSettingsRoute = lazy(async () => ({
  default: (await import('@/features/questionnaires/routes/questionnaire-settings-route'))
    .QuestionnaireSettingsRoute,
}));
const QuestionnaireTemplatesRoute = lazy(async () => ({
  default: (await import('@/features/questionnaires/routes/questionnaire-templates-route'))
    .QuestionnaireTemplatesRoute,
}));
const CompanyRoute = lazy(async () => ({
  default: (await import('@/features/settings/routes/company-route')).CompanyRoute,
}));
const AddressTypesRoute = lazy(async () => ({
  default: (await import('@/features/settings/routes/address-types-route')).AddressTypesRoute,
}));
const ContactResponsibilitiesRoute = lazy(async () => ({
  default: (await import('@/features/settings/routes/contact-responsibilities-route'))
    .ContactResponsibilitiesRoute,
}));
const PriceCalculationsRoute = lazy(async () => ({
  default: (await import('@/features/price-calculations')).PriceCalculationsRoute,
}));
const PriceGroupsRoute = lazy(async () => ({
  default: (await import('@/features/settings/routes/price-groups-route')).PriceGroupsRoute,
}));
const BusinessTermsRoute = lazy(async () => ({
  default: (await import('@/features/settings/routes/business-terms-route')).BusinessTermsRoute,
}));
const CustomerCategoriesRoute = lazy(async () => ({
  default: (await import('@/features/settings/routes/customer-categories-route'))
    .CustomerCategoriesRoute,
}));
const CustomerHeaderLayoutRoute = lazy(async () => ({
  default: (await import('@/features/settings/routes/customer-header-layout-route'))
    .CustomerHeaderLayoutRoute,
}));
const McpConnectionsRoute = lazy(async () => ({
  default: (await import('@/features/mcp')).McpConnectionsRoute,
}));
const McpAuthorizeRoute = lazy(async () => ({
  default: (await import('@/features/mcp')).McpAuthorizeRoute,
}));
const SettingsHubRoute = lazy(async () => ({
  default: (await import('@/features/settings/routes/settings-hub-route')).SettingsHubRoute,
}));
const PackagingWeightsRoute = lazy(async () => ({
  default: (await import('@/features/settings/routes/packaging-weights-route'))
    .PackagingWeightsRoute,
}));
const TranslationGlossaryRoute = lazy(async () => ({
  default: (await import('@/features/settings/routes/translation-glossary-route'))
    .TranslationGlossaryRoute,
}));
const SetupWizardRoute = lazy(async () => ({
  default: (await import('@/features/setup/routes/setup-wizard-route')).SetupWizardRoute,
}));
const SalesAssistantAnalyticsRoute = lazy(async () => ({
  default: (await import('@/features/sales-assistant/routes/sales-assistant-analytics-route'))
    .SalesAssistantAnalyticsRoute,
}));
const SalesAssistantRoute = lazy(async () => ({
  default: (await import('@/features/sales-assistant/routes/sales-assistant-route'))
    .SalesAssistantRoute,
}));
const SalesRuleAdminRoute = lazy(async () => ({
  default: (await import('@/features/sales-assistant/routes/sales-rule-admin-route'))
    .SalesRuleAdminRoute,
}));
const ProfileRoute = lazy(async () => ({
  default: (await import('@/features/users/routes/profile-route')).ProfileRoute,
}));
const RolesRoute = lazy(async () => ({
  default: (await import('@/features/users/routes/roles-route')).RolesRoute,
}));
const SecurityLogRoute = lazy(async () => ({
  default: (await import('@/features/users/routes/security-log-route')).SecurityLogRoute,
}));
const SecurityRoute = lazy(async () => ({
  default: (await import('@/features/users/routes/security-route')).SecurityRoute,
}));
const UsersRoute = lazy(async () => ({
  default: (await import('@/features/users/routes/users-route')).UsersRoute,
}));
const PortalAcceptRoute = lazy(async () => ({
  default: (await import('@/features/customer-portal/routes/portal-accept-route'))
    .PortalAcceptRoute,
}));
const PortalAccountRoute = lazy(async () => ({
  default: (await import('@/features/customer-portal/routes/portal-account-route'))
    .PortalAccountRoute,
}));
const PortalCartRoute = lazy(async () => ({
  default: (await import('@/features/customer-portal/routes/portal-cart-route')).PortalCartRoute,
}));
const PortalCatalogRoute = lazy(async () => ({
  default: (await import('@/features/customer-portal/routes/portal-catalog-route'))
    .PortalCatalogRoute,
}));
const PortalDashboardRoute = lazy(async () => ({
  default: (await import('@/features/customer-portal/routes/portal-dashboard-route'))
    .PortalDashboardRoute,
}));
const PortalDocumentDetailRoute = lazy(async () => ({
  default: (await import('@/features/customer-portal/routes/portal-document-detail-route'))
    .PortalDocumentDetailRoute,
}));
const PortalDocumentsRoute = lazy(async () => ({
  default: (await import('@/features/customer-portal/routes/portal-documents-route'))
    .PortalDocumentsRoute,
}));
const PortalDunningDetailRoute = lazy(async () => ({
  default: (await import('@/features/customer-portal/routes/portal-dunning-detail-route'))
    .PortalDunningDetailRoute,
}));
const PortalDunningRoute = lazy(async () => ({
  default: (await import('@/features/customer-portal/routes/portal-dunning-route'))
    .PortalDunningRoute,
}));
const PortalFilesRoute = lazy(async () => ({
  default: (await import('@/features/customer-portal/routes/portal-files-route')).PortalFilesRoute,
}));
const PortalLayout = lazy(async () => ({
  default: (await import('@/features/customer-portal/routes/portal-layout')).PortalLayout,
}));
const PortalLoginRoute = lazy(async () => ({
  default: (await import('@/features/customer-portal/routes/portal-login-route')).PortalLoginRoute,
}));
const PortalResetRoute = lazy(async () => ({
  default: (await import('@/features/customer-portal/routes/portal-reset-route')).PortalResetRoute,
}));
const PortalTeamRoute = lazy(async () => ({
  default: (await import('@/features/customer-portal/routes/portal-team-route')).PortalTeamRoute,
}));

const AccountingSettingsRoute = lazy(async () => ({
  default: (await import('@/features/accounting/routes/accounting-settings-route'))
    .AccountingSettingsRoute,
}));
const DatevRoute = lazy(async () => ({
  default: (await import('@/features/accounting/routes/datev-route')).DatevRoute,
}));
const IntrastatRoute = lazy(async () => ({
  default: (await import('@/features/accounting/routes/intrastat-route')).IntrastatRoute,
}));
const BankAccountsRoute = lazy(async () => ({
  default: (await import('@/features/banking/routes/bank-accounts-route')).BankAccountsRoute,
}));
const BankPaymentsRoute = lazy(async () => ({
  default: (await import('@/features/banking/routes/bank-payments-route')).BankPaymentsRoute,
}));
const BankReconciliationRoute = lazy(async () => ({
  default: (await import('@/features/banking/routes/bank-reconciliation-route'))
    .BankReconciliationRoute,
}));
const BankTransactionsRoute = lazy(async () => ({
  default: (await import('@/features/banking/routes/bank-transactions-route'))
    .BankTransactionsRoute,
}));
const BankingSettingsRoute = lazy(async () => ({
  default: (await import('@/features/banking/routes/banking-settings-route')).BankingSettingsRoute,
}));
const AnalyticsRoute = lazy(async () => ({
  default: (await import('@/features/analytics/routes/analytics-route')).AnalyticsRoute,
}));
const DmsArchiveRoute = lazy(async () => ({
  default: (await import('@/features/dms/routes/dms-archive-route')).DmsArchiveRoute,
}));
const DunningRoute = lazy(async () => ({
  default: (await import('@/features/dunning/routes/dunning-route')).DunningRoute,
}));
const GoodsReceiptTerminalRoute = lazy(async () => ({
  default: (await import('@/features/procurement/routes/goods-receipt-terminal-route'))
    .GoodsReceiptTerminalRoute,
}));
const GoodsReceiptsRoute = lazy(async () => ({
  default: (await import('@/features/procurement/routes/goods-receipts-route')).GoodsReceiptsRoute,
}));
const PurchaseOrderEditorRoute = lazy(async () => ({
  default: (await import('@/features/procurement/routes/purchase-order-editor-route'))
    .PurchaseOrderEditorRoute,
}));
const PurchaseOrdersRoute = lazy(async () => ({
  default: (await import('@/features/procurement/routes/purchase-orders-route'))
    .PurchaseOrdersRoute,
}));
const VendorInvoiceMatchRoute = lazy(async () => ({
  default: (await import('@/features/procurement/routes/vendor-invoice-match-route'))
    .VendorInvoiceMatchRoute,
}));
const VendorInvoicesRoute = lazy(async () => ({
  default: (await import('@/features/procurement/routes/vendor-invoices-route'))
    .VendorInvoicesRoute,
}));
const LabelDesignerRoute = lazy(async () => ({
  default: (await import('@/features/pdf-designer/routes/label-designer-route')).LabelDesignerRoute,
}));
const LabelTemplatesRoute = lazy(async () => ({
  default: (await import('@/features/pdf-designer/routes/label-templates-route'))
    .LabelTemplatesRoute,
}));
const PdfDesignerRoute = lazy(async () => ({
  default: (await import('@/features/pdf-designer/routes/pdf-designer-route')).PdfDesignerRoute,
}));
const PdfTemplatesRoute = lazy(async () => ({
  default: (await import('@/features/pdf-designer/routes/pdf-templates-route')).PdfTemplatesRoute,
}));
const AdminTelephonyAiRoute = lazy(async () => ({
  default: (await import('@/features/telephony/routes/admin-telephony-ai-route'))
    .AdminTelephonyAiRoute,
}));
const WhatsAppAdminRoute = lazy(async () => ({
  default: (await import('@/features/whatsapp/routes/whatsapp-admin-route')).WhatsAppAdminRoute,
}));
const WhatsAppInboxRoute = lazy(async () => ({
  default: (await import('@/features/whatsapp/routes/whatsapp-inbox-route')).WhatsAppInboxRoute,
}));
const AdminFeedbackRoute = lazy(async () => ({
  default: (await import('@/features/feedback/routes/admin-feedback-route')).AdminFeedbackRoute,
}));
const AdminProductSpecificationsRoute = lazy(async () => ({
  default: (
    await import('@/features/product-specifications/routes/admin-product-specifications-route')
  ).AdminProductSpecificationsRoute,
}));
const SpecificationRevisionRoute = lazy(async () => ({
  default: (await import('@/features/product-specifications/routes/specification-revision-route'))
    .SpecificationRevisionRoute,
}));
const RecipeEditorRoute = lazy(async () => ({
  default: (await import('@/features/recipes/routes/recipe-editor-route')).RecipeEditorRoute,
}));
const RecipesRoute = lazy(async () => ({
  default: (await import('@/features/recipes/routes/recipes-route')).RecipesRoute,
}));
const BatchScalingRoute = lazy(async () => ({
  default: (await import('@/features/process-routings/routes/batch-scaling-route'))
    .BatchScalingRoute,
}));
const DeclarationCatalogRoute = lazy(async () => ({
  default: (await import('@/features/declarations/routes/declaration-catalog-route'))
    .DeclarationCatalogRoute,
}));
const DeclarationDetailRoute = lazy(async () => ({
  default: (await import('@/features/declarations/routes/declaration-detail-route'))
    .DeclarationDetailRoute,
}));
const DeclarationsRoute = lazy(async () => ({
  default: (await import('@/features/declarations/routes/declarations-route')).DeclarationsRoute,
}));
const AnalysisParametersRoute = lazy(async () => ({
  default: (await import('@/features/analysis/routes/analysis-parameters-route'))
    .AnalysisParametersRoute,
}));
const AnalysisProfileDetailRoute = lazy(async () => ({
  default: (await import('@/features/analysis/routes/analysis-profile-detail-route'))
    .AnalysisProfileDetailRoute,
}));
const AnalysisProfilesRoute = lazy(async () => ({
  default: (await import('@/features/analysis/routes/analysis-profiles-route'))
    .AnalysisProfilesRoute,
}));
const ProductionOrderDetailRoute = lazy(async () => ({
  default: (await import('@/features/manufacturing/routes/production-order-detail-route'))
    .ProductionOrderDetailRoute,
}));
const ProductionOrdersRoute = lazy(async () => ({
  default: (await import('@/features/manufacturing/routes/production-orders-route'))
    .ProductionOrdersRoute,
}));
const TraceabilityRoute = lazy(async () => ({
  default: (await import('@/features/traceability/routes/traceability-route')).TraceabilityRoute,
}));
const MrpRoute = lazy(async () => ({
  default: (await import('@/features/mrp/routes/mrp-route')).MrpRoute,
}));
const ChangeRequestDetailRoute = lazy(async () => ({
  default: (await import('@/features/change-control/routes/change-request-detail-route'))
    .ChangeRequestDetailRoute,
}));
const ChangeRequestsRoute = lazy(async () => ({
  default: (await import('@/features/change-control/routes/change-requests-route'))
    .ChangeRequestsRoute,
}));
const ProductDevelopmentDashboardRoute = lazy(async () => ({
  default: (
    await import('@/features/product-development-dashboard/routes/product-development-dashboard-route')
  ).ProductDevelopmentDashboardRoute,
}));
const AdminFeaturesRoute = lazy(async () => ({
  default: (await import('@/features/admin/routes/admin-features-route')).AdminFeaturesRoute,
}));

const CrmRecentActionsRoute = lazy(async () => ({
  default: (await import('@/features/customers/routes/crm-recent-actions-route'))
    .CrmRecentActionsRoute,
}));

export const router = createBrowserRouter([
  {
    path: '/newsletter/subscribe',
    element: <NewsletterSubscribeRoute />,
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: '/newsletter/confirm',
    element: <NewsletterConfirmRoute />,
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: '/newsletter/preferences',
    element: <NewsletterPreferencesRoute />,
    errorElement: <RouteErrorBoundary />,
  },
  { path: '/portal/login', element: <PortalLoginRoute />, errorElement: <RouteErrorBoundary /> },
  { path: '/portal/accept', element: <PortalAcceptRoute />, errorElement: <RouteErrorBoundary /> },
  { path: '/portal/reset', element: <PortalResetRoute />, errorElement: <RouteErrorBoundary /> },
  {
    path: '/portal',
    element: (
      <RequirePortalAuth>
        <PortalFeatureAvailabilityProvider>
          <PortalLayout />
        </PortalFeatureAvailabilityProvider>
      </RequirePortalAuth>
    ),
    errorElement: <RouteErrorBoundary />,
    children: [
      { index: true, element: <PortalDashboardRoute /> },
      { path: 'documents', element: <PortalDocumentsRoute /> },
      { path: 'documents/:id', element: <PortalDocumentDetailRoute /> },
      { path: 'dunning', element: <PortalDunningRoute /> },
      { path: 'dunning/:id', element: <PortalDunningDetailRoute /> },
      { path: 'files', element: <PortalFilesRoute /> },
      { path: 'catalog', element: <PortalCatalogRoute /> },
      { path: 'cart', element: <PortalCartRoute /> },
      { path: 'account', element: <PortalAccountRoute /> },
      { path: 'team', element: <PortalTeamRoute /> },
    ],
  },
  {
    path: '/field/runs/:id/customer',
    element: <CustomerQuestionnaireRoute />,
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: LOGIN_PATH,
    element: <LoginRoute />,
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: '/',
    element: (
      <RequireAuth>
        <FeatureAvailabilityProvider>
          <AppShell />
        </FeatureAvailabilityProvider>
      </RequireAuth>
    ),
    errorElement: <RouteErrorBoundary />,
    children: [
      { index: true, element: <DashboardRoute /> },
      { path: 'news', element: <ProductNewsRoute /> },
      {
        path: 'returns',
        element: (
          <PermissionGuard resource="RETURNS" action="READ">
            <ReturnsRoute />
          </PermissionGuard>
        ),
      },
      {
        path: 'reusable-containers',
        element: (
          <PermissionGuard resource="REUSABLE_CONTAINERS" action="READ">
            <ReusableContainersRoute />
          </PermissionGuard>
        ),
      },
      {
        path: 'picking',
        element: (
          <PermissionGuard resource="PICKING" action="READ">
            <PickingRoute />
          </PermissionGuard>
        ),
      },
      {
        path: 'picking/:id',
        element: (
          <PermissionGuard resource="PICKING" action="READ">
            <PickingDetailRoute />
          </PermissionGuard>
        ),
      },
      {
        path: 'inventory-sessions',
        element: (
          <PermissionGuard resource="INVENTORY_COUNTS" action="READ">
            <InventorySessionsRoute />
          </PermissionGuard>
        ),
      },
      {
        path: 'inventory-sessions/:id',
        element: (
          <PermissionGuard resource="INVENTORY_COUNTS" action="READ">
            <InventorySessionDetailRoute />
          </PermissionGuard>
        ),
      },
      {
        path: 'delivery-tours',
        element: (
          <PermissionGuard resource="DELIVERY_TOURS" action="READ">
            <DeliveryToursRoute />
          </PermissionGuard>
        ),
      },
      {
        path: 'delivery-tours/:id',
        element: (
          <PermissionGuard resource="DELIVERY_TOURS" action="READ">
            <DeliveryTourDetailRoute />
          </PermissionGuard>
        ),
      },
      {
        path: 'driver',
        element: (
          <PermissionGuard resource="DELIVERY_TOURS" action="USE">
            <DeliveryToursRoute driverOnly />
          </PermissionGuard>
        ),
      },
      {
        path: 'driver/:id',
        element: (
          <PermissionGuard resource="DELIVERY_TOURS" action="USE">
            <DeliveryTourDetailRoute driverOnly />
          </PermissionGuard>
        ),
      },
      {
        path: 'electronic-invoices',
        element: (
          <PermissionGuard resource="ELECTRONIC_INVOICES" action="READ">
            <ElectronicInvoicesRoute />
          </PermissionGuard>
        ),
      },
      {
        path: 'settings/operations',
        element: (
          <PermissionGuard resource="SETTINGS" action="READ">
            <OperationalSettingsRoute />
          </PermissionGuard>
        ),
      },
      {
        path: 'settings/electronic-invoices',
        element: <Navigate to="/settings/operations" replace />,
      },
      { path: 'partners', element: <PartnersRoute /> },
      { path: 'partners/map', element: <PartnerMapRoute /> },
      { path: 'crm/recent-actions', element: <CrmRecentActionsRoute /> },
      { path: 'partners/customers', element: <PartnerCustomersRoute /> },
      { path: 'partners/prospects', element: <PartnerProspectsRoute /> },
      { path: 'partners/suppliers', element: <PartnerSuppliersRoute /> },
      { path: 'partners/:id', element: <PartnerDetailRoute /> },
      { path: 'sales-assistant', element: <SalesAssistantRoute /> },
      {
        path: 'sales-assistant/analytics',
        element: (
          <PermissionGuard resource="SALES_ASSISTANT" action="USE">
            <PermissionGuard resource="SALES_ASSISTANT" action="VIEW_OWN_TASKS">
              <SalesAssistantAnalyticsRoute />
            </PermissionGuard>
          </PermissionGuard>
        ),
      },
      // Bestandslinks aus Belegen, Benachrichtigungen und Lesezeichen bleiben gültig.
      { path: 'customers', element: <Navigate to="/partners/customers" replace /> },
      { path: 'customers/:id', element: <LegacyCustomerRedirect /> },
      { path: 'products', element: <ProductsRoute /> },
      { path: 'products/:id', element: <ProductDetailRoute /> },
      { path: 'product-development', element: <ProductDevelopmentDashboardRoute /> },
      { path: 'product-development/recipes', element: <RecipesRoute /> },
      { path: 'product-development/recipes/:id', element: <RecipeEditorRoute /> },
      { path: 'product-development/batch-scaling', element: <BatchScalingRoute /> },
      { path: 'product-development/declarations', element: <DeclarationsRoute /> },
      {
        path: 'product-development/declarations/:id',
        element: <DeclarationDetailRoute />,
      },
      {
        path: 'product-development/declaration-catalog',
        element: <DeclarationCatalogRoute />,
      },
      { path: 'product-development/analysis', element: <AnalysisProfilesRoute /> },
      { path: 'product-development/labels', element: <LabelTemplatesRoute /> },
      { path: 'product-development/labels/:id', element: <LabelDesignerRoute /> },
      { path: 'product-development/change-control', element: <ChangeRequestsRoute /> },
      {
        path: 'product-development/change-control/:id',
        element: <ChangeRequestDetailRoute />,
      },
      {
        path: 'product-development/analysis/:id',
        element: <AnalysisProfileDetailRoute />,
      },
      {
        path: 'product-development/analysis-parameters',
        element: <AnalysisParametersRoute />,
      },
      { path: 'manufacturing/production-orders', element: <ProductionOrdersRoute /> },
      {
        path: 'manufacturing/production-orders/:id',
        element: <ProductionOrderDetailRoute />,
      },
      { path: 'manufacturing/traceability', element: <TraceabilityRoute /> },
      { path: 'manufacturing/mrp', element: <MrpRoute /> },
      {
        path: 'products/:productId/specifications/:specificationId/revisions/:revisionId',
        element: <SpecificationRevisionRoute />,
      },
      {
        path: 'products/pricing',
        element: (
          <PermissionGuard resource="PRODUCTS" action="READ">
            <PriceCalculationsRoute />
          </PermissionGuard>
        ),
      },
      {
        path: 'inventory/overview',
        element: (
          <PermissionGuard resource="BATCHES" action="READ">
            <InventoryOverviewRoute />
          </PermissionGuard>
        ),
      },
      {
        path: 'inventory/import',
        element: (
          <PermissionGuard resource="BATCHES" action="READ">
            <WarehouseImportRoute />
          </PermissionGuard>
        ),
      },
      { path: 'inventory/products/:id', element: <ProductDetailRoute /> },
      { path: 'inventory/batches/:id', element: <BatchDetailRoute /> },
      { path: 'documents', element: <DocumentsRoute /> },
      { path: 'legacy-documents', element: <LegacyDocumentsRoute /> },
      { path: 'legacy-documents/:id', element: <LegacyDocumentsRoute /> },
      { path: 'sales', element: <Navigate to="/sales/all" replace /> },
      {
        path: 'sales/quotes',
        element: <DocumentsRoute initialType="QUOTE" titleKey="nav.quotes" />,
      },
      {
        path: 'sales/orders',
        element: <DocumentsRoute initialType="ORDER" titleKey="nav.salesOrders" />,
      },
      {
        path: 'sales/delivery-notes',
        element: <DocumentsRoute initialType="DELIVERY_NOTE" titleKey="nav.deliveryNotes" />,
      },
      {
        path: 'sales/invoices',
        element: <DocumentsRoute initialType="INVOICE" titleKey="nav.invoices" />,
      },
      {
        path: 'sales/credit-notes',
        element: <DocumentsRoute initialType="CREDIT_NOTE" titleKey="nav.creditNotes" />,
      },
      {
        path: 'sales/all',
        element: <DocumentsRoute titleKey="nav.allTransactions" />,
      },
      {
        path: 'order-processing',
        element: <Navigate to="/sales/all" replace />,
      },
      {
        path: 'finance/bank-accounts',
        element: <BankAccountsRoute />,
      },
      {
        path: 'finance/bank-transactions',
        element: <BankTransactionsRoute />,
      },
      {
        path: 'finance/bank-reconciliation',
        element: <BankReconciliationRoute />,
      },
      {
        path: 'finance/payments',
        element: <BankPaymentsRoute />,
      },
      {
        path: 'finance/open-items',
        element: (
          <DocumentsRoute initialType="INVOICE" initialPayment="OPEN" titleKey="nav.openItems" />
        ),
      },
      { path: 'finance/dunning', element: <DunningRoute /> },
      { path: 'documents/new', element: <DocumentEditorRoute /> },
      { path: 'documents/:id', element: <DocumentEditorRoute /> },
      { path: 'documents/:id/edit', element: <DocumentEditorRoute /> },
      { path: 'dms', element: <DmsArchiveRoute /> },
      { path: 'field', element: <FieldCockpitRoute /> },
      { path: 'field/questionnaires', element: <Navigate to="/field" replace /> },
      { path: 'field/runs', element: <QuestionnaireRunsRoute /> },
      { path: 'field/runs/new', element: <QuestionnaireRunStartRoute /> },
      { path: 'field/runs/:id', element: <QuestionnaireRunRoute /> },
      { path: 'documents/archive', element: <Navigate to="/dms" replace /> },
      { path: 'procurement/orders', element: <PurchaseOrdersRoute /> },
      { path: 'procurement/orders/new', element: <PurchaseOrderEditorRoute /> },
      { path: 'procurement/orders/:id', element: <PurchaseOrderEditorRoute /> },
      { path: 'procurement/goods-receipts', element: <GoodsReceiptsRoute /> },
      { path: 'procurement/goods-receipts/new', element: <GoodsReceiptTerminalRoute /> },
      { path: 'procurement/invoices', element: <VendorInvoicesRoute /> },
      {
        path: 'procurement/vendor-invoices',
        element: <Navigate to="/procurement/invoices" replace />,
      },
      { path: 'procurement/vendor-invoices/:id/match', element: <VendorInvoiceMatchRoute /> },
      { path: 'whatsapp', element: <WhatsAppInboxRoute /> },
      { path: 'marketing', element: <NewsletterRoute initialView="overview" /> },
      { path: 'marketing/campaigns', element: <NewsletterRoute initialView="campaigns" /> },
      { path: 'marketing/templates', element: <NewsletterRoute initialView="templates" /> },
      { path: 'marketing/audiences', element: <NewsletterRoute initialView="audiences" /> },
      { path: 'marketing/imports', element: <NewsletterRoute initialView="imports" /> },
      { path: 'marketing/recipients', element: <NewsletterRoute initialView="recipients" /> },
      { path: 'marketing/consents', element: <NewsletterRoute initialView="consents" /> },
      {
        path: 'marketing/existing-customer',
        element: <NewsletterRoute initialView="existing_customer" />,
      },
      { path: 'marketing/suppression', element: <NewsletterRoute initialView="suppression" /> },
      {
        path: 'marketing/deliverability',
        element: <NewsletterRoute initialView="deliverability" />,
      },
      { path: 'marketing/analytics', element: <NewsletterRoute initialView="analytics" /> },
      { path: 'marketing/settings', element: <NewsletterRoute initialView="settings" /> },
      { path: 'analytics', element: <AnalyticsRoute /> },
      {
        path: 'analytics/employee-performance',
        element: <Navigate to="/analytics?tab=employee-performance" replace />,
      },
      { path: 'accounting/datev', element: <DatevRoute /> },
      { path: 'accounting/intrastat', element: <IntrastatRoute /> },
      // Vollflächige Arbeitsfläche mit eigener Werkzeugleiste samt Rückweg; das
      // Konsolen-Gerüst würde ihr nur Höhe nehmen.
      { path: 'settings/pdf-designer/:id', element: <PdfDesignerRoute /> },
      // Gemeinsames Gerüst aller Konfigurationsseiten: Die Navigation bleibt beim
      // Wechsel zwischen den Bereichen stehen, weil nur der `Outlet` getauscht wird.
      {
        element: <ConsoleLayout />,
        children: [
          { path: 'accounting/settings', element: <AccountingSettingsRoute /> },
          { path: 'settings', element: <SettingsHubRoute /> },
          { path: 'settings/chatgpt', element: <McpConnectionsRoute /> },
          { path: 'settings/chatgpt/authorize', element: <McpAuthorizeRoute /> },
          { path: 'settings/profile', element: <ProfileRoute /> },
          { path: 'settings/security', element: <SecurityRoute /> },
          { path: 'settings/notifications', element: <NotificationSettingsRoute /> },
          {
            path: 'settings/operations-alerts',
            element: (
              <PermissionGuard resource="OPERATIONS" action="READ">
                <OperationsAlertsRoute />
              </PermissionGuard>
            ),
          },
          {
            path: 'settings/sales-rules',
            element: (
              <PermissionGuard resource="SALES_RULES" action="READ">
                <SalesRuleAdminRoute />
              </PermissionGuard>
            ),
          },
          { path: 'settings/banking', element: <BankingSettingsRoute /> },
          { path: 'settings/packaging-weights', element: <PackagingWeightsRoute /> },
          { path: 'settings/company', element: <CompanyRoute /> },
          { path: 'settings/price-groups', element: <PriceGroupsRoute /> },
          { path: 'settings/address-types', element: <AddressTypesRoute /> },

          { path: 'settings/conditions', element: <BusinessTermsRoute /> },
          { path: 'settings/customer-categories', element: <CustomerCategoriesRoute /> },

          { path: 'settings/contact-responsibilities', element: <ContactResponsibilitiesRoute /> },
          {
            path: 'settings/translations',
            element: (
              <PermissionGuard resource="TRANSLATIONS" action="MANAGE">
                <TranslationGlossaryRoute />
              </PermissionGuard>
            ),
          },
          { path: 'settings/setup', element: <SetupWizardRoute /> },
          { path: 'settings/introduction', element: <IntroductionRoute /> },
          { path: 'settings/import', element: <Navigate to="/admin/data-hub" replace /> },
          { path: 'settings/pdf-designer', element: <PdfTemplatesRoute /> },
          {
            path: 'settings/document-headers',
            element: (
              <PermissionGuard resource="DOCUMENT_CUSTOMER_HEADERS" action="MANAGE_TEMPLATES">
                <CustomerHeaderLayoutRoute />
              </PermissionGuard>
            ),
          },
          { path: 'settings/users', element: <UsersRoute /> },
          { path: 'settings/roles', element: <RolesRoute /> },
          { path: 'settings/security-log', element: <SecurityLogRoute /> },
          {
            path: 'admin',
            element: <AdminLayout />,
            children: [
              { index: true, element: <Navigate to="/admin/system" replace /> },
              { path: 'system', element: <AdminSystemRoute /> },
              { path: 'features', element: <AdminFeaturesRoute /> },
              { path: 'custom-fields', element: <AdminCustomFieldsRoute /> },
              { path: 'audit-vault', element: <AdminAuditVaultRoute /> },
              { path: 'data-hub', element: <AdminDataHubRoute /> },
              { path: 'mail-backups', element: <AdminMailBackupsRoute /> },
              { path: 'backups', element: <AdminBackupsRoute /> },
              { path: 'error-logs', element: <AdminErrorLogsRoute /> },
              { path: 'feedback', element: <AdminFeedbackRoute /> },
              {
                path: 'product-specifications',
                element: <AdminProductSpecificationsRoute />,
              },
              { path: 'whatsapp', element: <WhatsAppAdminRoute /> },
              { path: 'telephony-ai', element: <AdminTelephonyAiRoute /> },
              { path: 'questionnaires', element: <QuestionnaireTemplatesRoute /> },
              { path: 'questionnaires/:id', element: <QuestionnaireBuilderRoute /> },
              { path: 'questionnaires/:id/builder', element: <QuestionnaireBuilderRoute /> },
              { path: 'questionnaire-settings', element: <QuestionnaireSettingsRoute /> },
              { path: 'questionnaire-results', element: <QuestionnaireResultsRoute /> },
              { path: 'questionnaire-results/:id', element: <QuestionnaireResultDetailRoute /> },
            ],
          },
        ],
      },
    ],
  },
]);
